/**
 * Reviewing an Amazon import (FR-38, FR-39).
 *
 * Thousands of order lines come in; a few dozen are worth a look. Each one
 * gets a labelled Accept or Reject, by tap or by key, and the batch is sent
 * together: one round trip per decision would make this unusable.
 *
 * Accepting is the decision. A line that needs nothing else is filed when the
 * batch is submitted (FR-39); one that needs a doctor's note, or predates the
 * HSA, waits on Home instead.
 *
 * Keys: A accept · R reject · J/K or arrows to move · Enter to submit.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api.js';
import { useApi, useAction } from '../hooks.js';
import { ErrorBox, Money, Notice, PageHead, Pill, Skeleton, formatDate } from '../ui.js';
import type { AppConfigResponse, CoveredPerson, ImportCandidate, ImportRun } from '../types.js';

type Decision = 'accept' | 'reject';

interface Outcome {
  accepted: number;
  filed: number;
  needs_review: number;
  rejected: number;
}

const VERDICT: Record<string, { label: string; tone?: 'ok' | 'warn' }> = {
  qualified: { label: 'Qualifies', tone: 'ok' },
  likely_qualified: { label: 'Probably qualifies' },
  dual_purpose: { label: 'Needs a doctor’s note', tone: 'warn' },
  not_qualified: { label: 'Probably not' },
};

export function ImportReview() {
  const { id } = useParams<{ id: string }>();
  const run = useApi<{ run: ImportRun }>(id ? `/api/imports/${id}` : null);
  const queue = useApi<{ items: ImportCandidate[] }>(
    id ? `/api/imports/${id}/candidates?state=pending&limit=100` : null,
  );
  const persons = useApi<{ persons: CoveredPerson[] }>('/api/persons');
  const config = useApi<AppConfigResponse>('/api/config');

  const [decisions, setDecisions] = useState<Map<number, Decision>>(new Map());
  const [cursor, setCursor] = useState(0);
  const [patient, setPatient] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const items = queue.data?.items ?? [];
  const people = (persons.data?.persons ?? []).filter((p) => p.active);
  const defaultPerson = config.data?.config.default_patient_id ?? '';
  const who = patient || defaultPerson;
  const accepting = [...decisions.values()].some((d) => d === 'accept');

  const decide = useCallback(
    (seq: number, decision: Decision) => {
      setDecisions((prev) => {
        const next = new Map(prev);
        if (next.get(seq) === decision) next.delete(seq);
        else next.set(seq, decision);
        return next;
      });
      setCursor((c) => Math.min(c + 1, Math.max(0, items.length - 1)));
    },
    [items.length],
  );

  const submit = useAction(async () => {
    const res = await api.post<Outcome>(`/api/imports/${id}/decide`, {
      default_patient_id: who || undefined,
      decisions: [...decisions.entries()].map(([seq, action]) => ({ seq, action })),
    });
    setOutcome(res);
    setDecisions(new Map());
    setCursor(0);
    queue.reload();
    run.reload();
  });

  const finish = useAction(async () => {
    await api.post(`/api/imports/${id}/commit`);
    run.reload();
  });

  const canSubmit = decisions.size > 0 && !submit.pending && (!accepting || who !== '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLSelectElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      const current = items[cursor];
      if (e.key === 'a' || e.key === 'A') {
        if (current) decide(current.seq, 'accept');
      } else if (e.key === 'r' || e.key === 'R') {
        if (current) decide(current.seq, 'reject');
      } else if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, items.length - 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === 'Enter' && canSubmit) {
        void submit.run();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, cursor, decide, submit, canSubmit]);

  if (run.error) return <ErrorBox error={run.error} />;
  if (!run.data || !queue.data) return <Skeleton rows={5} />;

  const theRun = run.data.run;
  const validation = theRun.validation;
  const failed = validation && !validation.ok;

  return (
    <>
      <PageHead title="Review the import" back={{ to: '/imports', label: 'Imports' }}>
        {theRun.stats.rows_parsed.toLocaleString('en-US')} lines read, {theRun.stats.candidates} worth a look.
      </PageHead>

      {/* FR-96 — the file checks, before anything commits. Spelled out only
          when one failed; a pass is one line. */}
      {failed ? (
        <Notice kind="stop" title="The file was not accepted, and nothing was imported">
          {validation.checks
            .filter((c) => !c.ok)
            .map((c) => (
              <div key={c.name}>{c.detail}</div>
            ))}
        </Notice>
      ) : !validation ? (
        <Notice title="Still checking the file">
          This usually takes under a minute.{' '}
          <button className="quiet" onClick={() => { run.reload(); queue.reload(); }}>
            Check again
          </button>
        </Notice>
      ) : null}

      {outcome ? (
        <Notice kind="ok" title="Done">
          {outcome.filed} filed
          {outcome.needs_review > 0 ? (
            <>
              , {outcome.needs_review} waiting on <Link to="/">Home</Link> for a closer look
            </>
          ) : null}
          {outcome.rejected > 0 ? `, ${outcome.rejected} rejected` : ''}.
        </Notice>
      ) : null}

      {items.length === 0 ? (
        validation && !failed ? (
          <div className="card stack tight">
            <p className="small muted">
              {theRun.imported_at ? 'This import is finished.' : 'Every line has a decision.'}
            </p>
            <div className="actions">
              {!theRun.imported_at ? (
                <button className="primary" onClick={() => void finish.run()} disabled={finish.pending}>
                  Finish this import
                </button>
              ) : null}
              <Link className="btn" to="/">
                Go to Home
              </Link>
            </div>
            {finish.error ? <ErrorBox error={finish.error} /> : null}
          </div>
        ) : null
      ) : (
        <>
          {/* PRD §6.3 — the export does not say who an item was for. */}
          <div className="field">
            <label htmlFor="patient">Who are these for?</label>
            <select id="patient" value={who} onChange={(e) => setPatient(e.target.value)}>
              <option value="">Choose…</option>
              {people.map((p) => (
                <option key={p.person_id} value={p.person_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
            <span className="hint">Applies to everything you accept in this batch.</span>
          </div>

          <div className="list">
            {items.map((c, i) => {
              const decision = decisions.get(c.seq);
              const verdict = VERDICT[c.classification] ?? { label: c.classification.replace(/_/g, ' ') };
              return (
                <div
                  key={c.seq}
                  className={`cand${i === cursor ? ' cursor' : ''}${decision === 'reject' ? ' rejected' : ''}`}
                  onClick={() => setCursor(i)}
                >
                  <div>
                    <div className="cand-title">{c.product_name}</div>
                    <div className="sub">
                      {formatDate(c.ship_date ?? c.order_date, { year: 'always' })} · {c.rationale}
                    </div>
                  </div>
                  <div className="cand-end">
                    <Money cents={c.amount_cents} />
                    <Pill tone={verdict.tone}>{verdict.label}</Pill>
                  </div>
                  <div className="seg" role="group" aria-label={`Decision for ${c.product_name}`}>
                    <button
                      className="yes"
                      aria-pressed={decision === 'accept'}
                      onClick={(e) => {
                        e.stopPropagation();
                        decide(c.seq, 'accept');
                      }}
                    >
                      Accept
                    </button>
                    <button
                      className="no"
                      aria-pressed={decision === 'reject'}
                      onClick={(e) => {
                        e.stopPropagation();
                        decide(c.seq, 'reject');
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {submit.error ? <ErrorBox error={submit.error} /> : null}

          <button className="primary big" onClick={() => void submit.run()} disabled={!canSubmit}>
            {submit.pending
              ? 'Saving…'
              : decisions.size === 0
                ? 'Accept or reject each line'
                : `Save ${decisions.size} decision${decisions.size === 1 ? '' : 's'}`}
          </button>
          {accepting && who === '' ? <p className="small tone-warn">Choose who these are for first.</p> : null}
          <p className="xs muted">
            Accepted lines are filed. Rejected products are skipped on future imports. Keys: A accept, R
            reject, J and K to move, Enter to save.
          </p>
        </>
      )}
    </>
  );
}
